/**
 * Converts an image file to ICO format for favicon
 */
export async function convertToFavicon(file: File): Promise<File> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    
    if (!ctx) {
      reject(new Error('Could not get canvas context'));
      return;
    }

    img.onload = () => {
      // Set canvas to 32x32 (standard favicon size)
      canvas.width = 32;
      canvas.height = 32;

      // Draw and scale image to 32x32
      ctx.drawImage(img, 0, 0, img.width, img.height, 0, 0, 32, 32);

      // Convert to blob
      canvas.toBlob((blob) => {
        if (blob) {
          // Create new file with .ico extension
          const icoFile = new File([blob], 'favicon.ico', {
            type: 'image/x-icon',
            lastModified: new Date().getTime()
          });
          resolve(icoFile);
        } else {
          reject(new Error('Could not convert image to ICO'));
        }
      }, 'image/png'); // Use PNG format for better quality
    };

    img.onerror = () => {
      reject(new Error('Could not load image'));
    };

    // Load image from file
    img.src = URL.createObjectURL(file);
  });
}

/**
 * Ensures image file is in PNG format without resizing
 */
export async function processImageFile(file: File): Promise<File> {
  // If file is already PNG, return it as-is
  if (file.type === 'image/png') {
    return file;
  }

  return new Promise((resolve, reject) => {
    const img = new Image();
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      reject(new Error('Could not get canvas context'));
      return;
    }

    img.onload = () => {
      // Use original dimensions
      canvas.width = img.width;
      canvas.height = img.height;

      // Draw image at original size
      ctx.drawImage(img, 0, 0);

      // Convert to blob
      canvas.toBlob((blob) => {
        if (blob) {
          // Keep original file name but ensure it's PNG
          const fileName = file.name.replace(/\.[^/.]+$/, '') + '.png';
          const processedFile = new File([blob], fileName, {
            type: 'image/png',
            lastModified: new Date().getTime()
          });
          resolve(processedFile);
        } else {
          reject(new Error('Could not process image'));
        }
      }, 'image/png', 1.0); // Use PNG format with maximum quality
    };

    img.onerror = () => {
      reject(new Error('Could not load image'));
    };

    img.src = URL.createObjectURL(file);
  });
}
